import React from "react";
import { Music, Flame, Mail, CheckCircle2 } from "lucide-react";

/**
 * BookingStatistics - Displays booking statistics cards
 */
export const KaraokeStatistics = ({ bookings, pendingBookings, confirmedBookings, onAutoCompletePast }) => {
  const totalRevenue = confirmedBookings
    .filter(b => b.status === 'paid' || b.status === 'completed')
    .reduce((sum, b) => sum + (b.price || 0), 0);

  return (
    <div className="brutal-card bg-gradient-to-r from-purple-50 to-indigo-50">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Music className="text-purple-500" /> Karaoke Booking Statistics
        </h3>
        {onAutoCompletePast && (
          <button
            onClick={onAutoCompletePast}
            className="px-4 py-2 bg-gray-600 text-white font-bold text-sm border-2 border-black hover:bg-gray-700 flex items-center gap-2"
            title="Mark all past paid bookings as completed"
            data-testid="auto-complete-past-btn"
          >
            <CheckCircle2 size={16} /> Auto Complete Past
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard value={bookings.length} label="Total Bookings" color="purple" />
        <StatCard value={pendingBookings.length} label="Pending Payment" color="amber" />
        <StatCard value={confirmedBookings.filter(b => b.status === 'paid').length} label="Confirmed" color="green" />
        <StatCard value={`$${totalRevenue}`} label="Total Revenue" color="blue" />
      </div>
      <p className="text-xs text-gray-500 mt-3 text-center">
        ⏰ Bookings auto-complete daily at 1:00 AM (SGT) for past sessions
      </p>
    </div>
  );
};

export const BBQStatistics = ({ bbqBookings, onTriggerUnpaidAlert }) => {
  const pending = bbqBookings.filter(b => b.status === 'pending').length;
  const confirmed = bbqBookings.filter(b => b.status === 'paid' || b.status === 'completed').length;
  const totalRevenue = bbqBookings
    .filter(b => b.status === 'paid' || b.status === 'completed')
    .reduce((sum, b) => sum + (b.price || 0), 0);

  return (
    <div className="brutal-card bg-gradient-to-r from-orange-50 to-amber-50">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Flame className="text-orange-500" /> BBQ Booking Statistics
        </h3>
        {onTriggerUnpaidAlert && (
          <button
            onClick={onTriggerUnpaidAlert}
            className="px-4 py-2 bg-amber-500 text-white font-bold text-sm border-2 border-black hover:bg-amber-600 flex items-center gap-2"
            title="Send alert email for unpaid bookings due in 3 days"
          >
            <Mail size={16} /> Send Unpaid Alert
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard value={bbqBookings.length} label="Total Bookings" color="orange" />
        <StatCard value={pending} label="Pending Payment" color="amber" />
        <StatCard value={confirmed} label="Confirmed" color="green" />
        <StatCard value={`$${totalRevenue}`} label="Total Revenue" color="purple" />
      </div>
      <p className="text-xs text-gray-500 mt-3 text-center">
        📧 Automated unpaid alert runs daily at 8:00 AM (SGT) for bookings due in 3 days
      </p>
    </div>
  );
};

const StatCard = ({ value, label, color }) => {
  const colorClasses = {
    purple: 'text-purple-600',
    amber: 'text-amber-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    orange: 'text-orange-600',
    red: 'text-red-600'
  };

  return (
    <div className="text-center p-4 bg-white border-2 border-black">
      <p className={`text-2xl font-bold ${colorClasses[color] || 'text-gray-600'}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

export default { KaraokeStatistics, BBQStatistics };
